import './HomeCharts.scss';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Card, Dropdown, DropdownButton, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ABILITY_MAP } from '../../constants';
import { ThemeContext } from '../../context/ThemeContext';
import { useApp } from '../../react';
import { Api } from '../../services';
import { DateTabs, Icon, Loading, Text } from '..';
import { Export } from '../utils';
import AssetChartEdit from './AssetChartEdit';
import Graph from './Graph';

async function fetchAsset(asset) {
  if (!asset) return;
  const data = Api.get(`v2/assets/${asset}`);
  return data;
}

async function getReadings(beacon, reading, startDate, endDate, params?) {
  const startDateStr = startDate.toISOString();
  const endDateStr = endDate.toISOString();
  const data = Api.get(
    `/beacons/${beacon.id}/readings/${reading.id}?start=${startDateStr}&end=${endDateStr}`,
    params,
  );
  return data;
}

async function exportReadings(
  beacon,
  reading,
  startDate,
  endDate,
  userTempPref,
  pressureUnit,
  params?,
) {
  const startDateStr = startDate.toISOString();
  const endDateStr = endDate.toISOString();

  const data = Api.get(
    `/beacons/${beacon.id}/readings/${reading.id}/export?useFahrenheit=${userTempPref}&pressureUnit=${pressureUnit}&start=${startDateStr}&end=${endDateStr}`,
    params,
  );
  return data;
}

function AssetChart({ type, info, edit, index, isValid, remove, updateWidget }) {
  const app = useApp();
  const { theme } = useContext(ThemeContext);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [csvLoading, setCsvLoading] = useState<boolean>(false);
  const [assetId, setAssetId] = useState<any>(info ? info.asset : null);
  const [beaconId, setBeaconId] = useState<any>(info ? info.beacon : null);
  const [readingId, setReadingId] = useState<any>(info ? info.reading : null);
  const [curBeacon, setCurBeacon] = useState<any>(null);
  const [curReading, setCurReading] = useState<any>(null);
  const [dataOption, setDataOption] = useState('mean');
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [userTempPref, setUserTempPref] = useState<boolean>(false);

  const DATA_OPTIONS = [
    { label: 'Standard Deviation', key: 'standardDeviation' },
    { label: 'Mean', key: 'mean' },
    { label: 'Max', key: 'max' },
    { label: 'Value', key: 'value' },
    { label: 'N', key: 'n' },
    { label: 'Min', key: 'min' },
  ];
  const tempPreference = useSelector((state) => state.userPrefs.temp);
  const pressurePreference = useSelector((state) => state.userPrefs.pressure);
  const location = useLocation();

  const {
    data: curAsset,
    refetch: curAssetRefetch,
    isError: assetError,
  } = useQuery<any>(
    [`asset-chart-${index}`],
    async () => {
      setIsLoading(true); // <- START loading
      return await fetchAsset(assetId);
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: () => {
        setIsLoading(false); // <- STOP loading on success
      },
      onError: () => {
        setIsLoading(false); // <- Stop loading even if it errors
      },
    },
  );

  useEffect(() => {
    curAssetRefetch();
  }, [location.pathname, theme]); // this now triggers when route changes (e.g., tabs)

  useEffect(() => {
    if (assetId && beaconId && readingId) {
      updateWidget({
        index: index,
        asset: assetId,
        beacon: beaconId,
        reading: readingId,
        isValid: true,
      });
    } else {
      updateWidget({ index: index, isValid: false });
    }
  }, [index, assetId, beaconId, readingId, updateWidget]);

  useEffect(() => {
    curAssetRefetch();
  }, [assetId, curAssetRefetch]);

  useEffect(() => {
    if (curAsset && beaconId) {
      const matched = curAsset.data.beacons.find((beacon) => beacon.id === beaconId);
      if (matched) {
        setCurBeacon(matched);
      } else {
        const fallback = curAsset.data.beacons.find((b) => b.abilities?.length > 0);
        if (fallback && !edit) {
          setCurBeacon(fallback);
          setBeaconId(fallback.id);
          const firstReading = fallback.readings[0];
          if (firstReading) {
            setCurReading(firstReading);
            setReadingId(firstReading.id);
          }
        } else {
          setCurBeacon(null);
          setCurReading(null);
        }
      }
    }
  }, [curAsset, beaconId, edit]);

  useEffect(() => {
    if (curBeacon && readingId) {
      let newReading = curBeacon.abilities.filter((ability) => {
        return ability.id === readingId;
      });
      setCurReading(newReading[0]);
    }
  }, [curBeacon, readingId]);

  useEffect(() => {
    if (!curBeacon || !curReading || !startDate || !endDate || edit) return;

    if (!curReading.id) {
      console.warn('Missing abilityId, skipping getReadings');
      return;
    }

    getReadings(curBeacon, curReading, startDate, endDate, userTempPref).then((res) => {
      setChartData(res);
    });
  }, [
    curBeacon,
    curReading,
    startDate,
    endDate,
    userTempPref,
    edit,
    tempPreference,
    pressurePreference,
  ]);

  const getExport = async (exportType) => {
    try {
      const resp = await Api.get('/users');

      let updatedUserTempPref = userTempPref;
      if (
        resp.data.temperatureUnit === ('Fahrenheit' || 'F') &&
        curReading.name === 'Temperature'
      ) {
        const result = await Swal.fire({
          title: 'Select which units to export Temperature Reading:',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Export °F',
          cancelButtonText: 'Export °C',
        });

        updatedUserTempPref = result.isConfirmed;
        setUserTempPref(updatedUserTempPref);
      } else {
        updatedUserTempPref = false;
      }

      setCsvLoading(true);
      const params = {
        responseType: 'blob',
        headers: { Accept: exportType },
      };
      const res = await exportReadings(
        curBeacon,
        curReading,
        startDate,
        endDate,
        updatedUserTempPref,
        pressurePreference,
        params,
      );

      setTimeout(() => {
        setCsvLoading(false);
      }, 1000);

      return res;
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const onChangeReadingsDate = useCallback(async (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
  }, []);

  // Select chart data type
  const onChangeDataOption = (index) => {
    setDataOption(DATA_OPTIONS[index].key);
  };

  const renderReadingsCharts = () => {
    if (!chartData.data.length)
      return (
        <div className="d-flex flex-column align-items-center justify-content-center mt-5 mb-5 w-100 no-reading-container">
          <Icon
            name="analytics"
            variant="dark"
            size="6x"
            className="mb-3"
            disabled={false}
            showPointer={false}
          />
          <Text className="fs-md">
            No readings found for this Beacon and Ability in the selected date range.
          </Text>
        </div>
      );

    const readings = chartData.data.sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));
    const abilityFormatter = ABILITY_MAP[curReading.name] || ABILITY_MAP.Generic;

    const data = readings.map((reading) => {
      const val = abilityFormatter.numValue(reading, dataOption);
      return val;
    });

    return (
      <Graph
        title={`${curBeacon.name} - ${curReading.name}`}
        x={readings.map((reading) => new Date(reading.timestamp))}
        y={data}
      />
    );
  };

  const renderTooltip = (tip) => <Tooltip id="button-tooltip">{tip}</Tooltip>;

  if (isLoading && !edit) {
    return (
      <div className="card-display m-auto">
        <Loading />
      </div>
    );
  }

  if (edit) {
    return (
      <AssetChartEdit
        index={index}
        curAsset={curAsset}
        assetId={assetId}
        beaconId={beaconId}
        readingId={readingId}
        isValid={isValid}
        setAssetId={setAssetId}
        setBeaconId={setBeaconId}
        setReadingId={setReadingId}
        remove={remove}
      />
    );
  }

  // If Deleted Asset
  if (assetError || (!curAsset && !edit)) {
    return (
      <div className="card-display m-auto">
        <div className="d-grid text-center">
          <i className="ignore-click sonarIcon sonarIcon-warning align-sub fs-2" />
          <Text className="fs-md fw-semibold text-danger mt-2">Asset deleted.</Text>
          <Text className="fs-md fw-semibold text-danger mb-3">
            Edit Dashboard to remove this chart.
          </Text>
          <Button
            variant="primary"
            href="/dashboard-edit"
            className="delete-asset-chart-btn m-auto"
          >
            Edit Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // If Beacons were removed from Asset
  if (curAsset && curAsset.data?.beacons?.length === 0 && !edit && !isLoading) {
    return (
      <>
        <Card.Header className="d-flex flex-row justify-content-between align-items-left reading-container-header">
          <div className="d-flex flex-row justify-content-left align-items-left">
            <a
              href={`/assets/${curAsset.data.id}`}
              className="fs-md fw-semibold ignore-click btn btn-light"
              onClick={(e) => {
                e.preventDefault();
                history.push(`/assets/${curAsset.data.id}`);
              }}
            >
              {curAsset.data.name}
            </a>
          </div>
        </Card.Header>
        <div className="card-display asset-chart-graph-err-msg">
          <div className="d-grid text-center">
            <i className="ignore-click sonarIcon sonarIcon-warning align-sub fs-2" />
            <Text className="fs-md fw-semibold text-danger mt-2">
              This Asset has no Assigned Beacons.
            </Text>
            <Text className="fs-md fw-semibold text-danger">
              Assign a Beacon to {curAsset.data.name} to generate graph.
            </Text>
          </div>
        </div>
      </>
    );
  }

  // If Asset has Beacons but NO Readings
  if (curAsset && !curBeacon && !curReading && !isLoading) {
    return (
      <>
        <Card.Header className="d-flex flex-row justify-content-between align-items-left reading-container-header">
          <div className="d-flex flex-row justify-content-left align-items-left">
            <a
              href={`/assets/${curAsset.data.id}`}
              className="fs-md fw-semibold ignore-click btn btn-light"
              onClick={(e) => {
                e.preventDefault();
                history.push(`/assets/${curAsset.data.id}`);
              }}
            >
              {curAsset.data.name}
            </a>
          </div>
        </Card.Header>
        <div className="card-display asset-chart-graph-err-msg">
          <div className="d-grid text-center">
            <i className="ignore-click sonarIcon sonarIcon-warning align-sub fs-2" />
            <Text className="fs-md fw-semibold text-danger mt-2">
              The Beacons attached have no available Readings to display.
            </Text>
            <Text className="fs-md fw-semibold text-danger mt-2">
              Assign a Beacon with Ability Readings to generate chart.
            </Text>
          </div>
        </div>
      </>
    );
  }

  if (curAsset && curBeacon && !isLoading) {
    return (
      <div className={isLoading ? 'card-load' : 'card-display'}>
        <Card.Header className="d-flex flex-row justify-content-between align-items-left reading-container-header">
          <div className="d-flex flex-row justify-content-left align-items-left">
            <a
              href={`/assets/${curAsset.data.id}`}
              className="fs-md fw-semibold ignore-click btn btn-light"
              onClick={(e) => {
                e.preventDefault();
                history.push(`/assets/${curAsset.data.id}`);
              }}
            >
              {curAsset.data.name}
            </a>
          </div>
          {edit && (
            <Button
              className="delete-icon ignore-click"
              variant="light"
              onClick={() => {
                remove(index);
              }}
            >
              <i className="ignore-click sonarIcon sonarIcon-add sonarIcon-rotate-45 align-sub fs-lg" />
            </Button>
          )}
        </Card.Header>

        {curBeacon && (
          <Card.Body className="d-flex flex-row flex-wrap justify-content-between align-items-center pb-0">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip('All Health Check graphs are locked to N values.')}
              >
                <span className="d-inline-block info-icon-span me-2">
                  <div className="info-icon-div">
                    <Icon
                      className="align-sub ignore-click"
                      name="info"
                      variant="dark"
                      disabled={false}
                      size="md"
                    />
                  </div>
                </span>
              </OverlayTrigger>
              <DropdownButton
                className="me-3 ignore-click"
                variant="secondary"
                title="Beacons"
                id="Beacons"
              >
                {curAsset.data.beacons
                  .filter(
                    (beacon) => Array.isArray(beacon.abilities) && beacon.abilities.length > 0,
                  )

                  .map((beacon) => (
                    <Dropdown.Item
                      as="button"
                      key={beacon.id}
                      active={curBeacon.id === beacon.id}
                      onClick={() => {
                        setCurBeacon(beacon);
                        if (beacon.abilities && beacon.abilities.length > 0) {
                          setCurReading(beacon.abilities[0]);
                          setReadingId(beacon.abilities[0].id);
                        } else {
                          setCurReading(null);
                          setReadingId(null);
                        }
                      }}
                    >
                      {beacon.name}
                    </Dropdown.Item>
                  ))}
              </DropdownButton>

              <DropdownButton
                className="me-3 ignore-click"
                variant="secondary"
                title="Abilites"
                id="Abilites"
              >
                {curBeacon.abilities.map((ability) => (
                  <Dropdown.Item
                    as="button"
                    key={ability.id}
                    active={readingId === ability.id}
                    onClick={() => {
                      setCurReading(ability);
                      setReadingId(ability.id);
                    }}
                  >
                    {ability.name}
                  </Dropdown.Item>
                ))}
              </DropdownButton>

              <DropdownButton
                className="me-3 ignore-click"
                variant="secondary"
                title="Data Options"
                id="Data Options"
              >
                {DATA_OPTIONS.map((option, index) => (
                  <Dropdown.Item
                    as="button"
                    key={index}
                    active={option.key === dataOption}
                    onClick={() => {
                      onChangeDataOption(index);
                    }}
                  >
                    {option.label}
                  </Dropdown.Item>
                ))}
              </DropdownButton>
              <Export
                filename={`${curBeacon.name}_${curReading?.name}_Export`}
                options={['CSV', 'JSON']}
                csvLoading={csvLoading}
                getExport={async (option) => {
                  return await getExport(option);
                }}
                advancedAsset={undefined}
                addRun={false}
              />
            </div>
            {curBeacon && (
              <div className="d-flex flex-row justify-content-between">
                <DateTabs onChangeDate={onChangeReadingsDate} />
              </div>
            )}
          </Card.Body>
        )}

        <Card.Body className="overflow-hidden d-flex flex-row justify-content-between align-items-center">
          {isLoading && <Loading />}
          {!isLoading && curBeacon && curReading && chartData && renderReadingsCharts()}
        </Card.Body>
      </div>
    );
  }
}

export default AssetChart;
